import React from 'react';
import { type StyleProp, type TextStyle } from 'react-native';
import Animated, { SharedValue } from 'react-native-reanimated';

import { useTypewriterAnimation, useTypewriterAnimationCharStyle } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders text with a typewriter animation, revealing one character at a time.
 * Each character is rendered as an `Animated.Text` with animated opacity.
 *
 * Designed to be nested inside a parent `Text` component that provides
 * container styling (font, color, etc.).
 *
 * @example
 * ```tsx
 * <Text style={{ fontSize: 32 }}>
 *   <TypewriterText text="Hello, world!" charDelayMs={50} />
 * </Text>
 * ```
 */
export const TypewriterText = (props: Props) => {
  const { text, charDelayMs, style, maxFontSizeMultiplier } = props;

  const characters = text.split('');

  // ─────────────────────────────────────────────────────────────────────────────

  const animatedIndex = useTypewriterAnimation({ text, charDelayMs });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {characters.map((char, index) => (
        <TypewriterChar
          key={index}
          char={char}
          index={index}
          animatedIndex={animatedIndex}
          style={style}
          maxFontSizeMultiplier={maxFontSizeMultiplier}
        />
      ))}
    </>
  );
};

// ─── Components ─────────────────────────────────────────────────────────────

const TypewriterChar = (props: TypewriterCharProps) => {
  const { char, index, animatedIndex, style, maxFontSizeMultiplier } = props;

  const animatedStyle = useTypewriterAnimationCharStyle({
    index,
    animatedIndex,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.Text
      style={[animatedStyle, style]}
      accessible={false}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
    >
      {char}
    </Animated.Text>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  text: string;
  charDelayMs?: number;
  style?: StyleProp<TextStyle>;
  maxFontSizeMultiplier?: number;
};

type TypewriterCharProps = {
  char: string;
  index: number;
  animatedIndex: SharedValue<number>;
  style?: StyleProp<TextStyle>;
  maxFontSizeMultiplier?: number;
};
